import type { WorklistSurfaceType } from '../types';
import { WorklistSurface } from '../types';
import {
  WorklistColumnId,
  WorklistColumn,
  WorklistColumnsBySurface,
  WorklistColumnsBySurfaceInput,
  MatchType,
} from 'generated/graphql';
import {
  DEFAULT_COLUMNS,
  DEFAULT_VIEWER_STUDIES_DIALOG_COLUMNS,
  DEFAULT_COLUMN_ORDERING,
  DEFAULT_PATIENT_JACKET_PRIORS_COLUMNS,
} from './constants';

import clone from 'ramda/es/clone';
import { removeTypeName } from 'modules/analytics/util';
import type { ColumnDef, GroupColumnDef } from '@tanstack/react-table';
import { Colors } from 'styles';
import Tooltip from 'common/ui/Tooltip';
import { WorklistItemData } from '../hooks/useGenerateWorklistTableColumnConfig';
import { isEmptyStringOrNull } from 'utils/string';

export const iconForMatchType = (
  matchType: MatchType,
  showTooltip?: boolean
): React.ReactElement => {
  const size = '1.5rem';
  const color = iconColorForMatchType(matchType);
  const borderWidth = '2px';

  const baseCSS = `width: ${size};
          height: ${size};
          border-radius: 50%;`;

  const mrnIcon = (
    <div
      data-testid={`match-type-full-circle-${color}`}
      css={`
        ${baseCSS}
        background-color:${color};
      `}
    />
  );

  const attrMatchIcon = (
    <div
      data-testid={`match-type-half-circle-${color}`}
      css={`
        ${baseCSS}
        border: ${borderWidth} solid ${color};
        position: relative;
        transform: scale(-1);
      `}
    >
      <div
        css={`
          position: absolute;
          top: -${borderWidth};
          left: -${borderWidth};
          height: calc(${size} / 2 - 1px);
          width: ${size};
          border-radius: calc(${size} / 2) calc(${size} / 2) 0 0;
          background-color: ${color};
        `}
      />
    </div>
  );

  const partialAttrMatchIcon = (
    <div
      data-testid={`match-type-empty-circle-${color}`}
      css={`
        ${baseCSS}
        border: 2px solid ${color};
      `}
    />
  );

  let icon = <div />;

  switch (matchType) {
    case MatchType.Mrn:
      icon = mrnIcon;
      break;
    case MatchType.PatientAttributes:
      icon = attrMatchIcon;
      break;
    case MatchType.PatientAttributesPartial:
      icon = partialAttrMatchIcon;
      break;
    default:
      throw new Error(`Unrecognized match type: ${matchType}`);
  }

  if (showTooltip === true) {
    return (
      <Tooltip color={Colors.gray0} placement="top" arrow content={labelForMatchType(matchType)}>
        {icon}
      </Tooltip>
    );
  }

  return icon;
};

export const iconColorForMatchType = (matchType: MatchType): string => {
  switch (matchType) {
    case MatchType.Mrn:
      return Colors.green4;
    case MatchType.PatientAttributes:
      return Colors.yellow4;
    case MatchType.PatientAttributesPartial:
      return Colors.gray6;
    default:
      return Colors.gray6;
  }
};

export const labelForMatchType = (matchType: MatchType): string => {
  switch (matchType) {
    case MatchType.Mrn:
      return 'Full patient match';
    case MatchType.PatientAttributes:
    case MatchType.PatientAttributesPartial:
      return 'Partial patient match';
    default:
      return 'No patient match';
  }
};

export const getDefaultColumnOrderingForSurface = (
  surface: WorklistSurfaceType
): Array<WorklistColumnId> => {
  switch (surface) {
    case WorklistSurface.CUSTOM_WORKLIST:
      return DEFAULT_COLUMN_ORDERING;
    case WorklistSurface.STUDIES_DIALOG:
      return DEFAULT_VIEWER_STUDIES_DIALOG_COLUMNS;
    case WorklistSurface.PATIENT_JACKET_PRIORS:
    case WorklistSurface.SEARCH_JACKET_PRIORS:
      return DEFAULT_PATIENT_JACKET_PRIORS_COLUMNS;
    default:
      return DEFAULT_COLUMN_ORDERING;
  }
};

export const getDefaultVisibleColumnsForSurface = (
  surface: WorklistSurfaceType
): Array<WorklistColumnId> => {
  switch (surface) {
    case WorklistSurface.CUSTOM_WORKLIST:
      return DEFAULT_COLUMNS;
    case WorklistSurface.STUDIES_DIALOG:
      return DEFAULT_VIEWER_STUDIES_DIALOG_COLUMNS;
    case WorklistSurface.PATIENT_JACKET_PRIORS:
    case WorklistSurface.SEARCH_JACKET_PRIORS:
      return DEFAULT_PATIENT_JACKET_PRIORS_COLUMNS;
    default:
      return DEFAULT_COLUMNS;
  }
};

export const prepareColumnsBySurfaceMutationVariables = (
  surface: WorklistSurfaceType,
  inputColumns: Array<WorklistColumn>,
  columnsBySurface: ReadonlyArray<WorklistColumnsBySurface>
): {
  columnsBySurfaceInput: Array<WorklistColumnsBySurfaceInput>;
  columnsBySurfaceResponse: Array<WorklistColumnsBySurface>;
} => {
  const columnsBySurfaceSettings = clone(columnsBySurface);
  const surfaceIndex = columnsBySurfaceSettings.findIndex((item) => item.surface === surface);

  let columnsBySurfaceResponse = [];

  if (surfaceIndex === -1) {
    columnsBySurfaceResponse = [
      ...columnsBySurfaceSettings,
      {
        __typename: 'WorklistColumnsBySurface' as const,
        surface: surface,
        columns: inputColumns,
      },
    ];
  } else {
    columnsBySurfaceResponse = [
      ...columnsBySurfaceSettings.slice(0, surfaceIndex),
      {
        __typename: 'WorklistColumnsBySurface' as const,
        surface,
        columns: inputColumns,
      },
      ...columnsBySurfaceSettings.slice(surfaceIndex + 1),
    ];
  }

  return {
    columnsBySurfaceInput: removeTypeName(columnsBySurfaceResponse),
    columnsBySurfaceResponse,
  };
};

export const getTableGroupColumns = <TData,>(
  columns: ReadonlyArray<ColumnDef<TData>>
): ReadonlyArray<ColumnDef<TData>> => {
  return (columns.find((item) => item.id === 'table') as GroupColumnDef<TData>)?.columns ?? [];
};

export const getVisibleColumnIDs = <TData,>({
  visibleColumnsForTable,
}: {
  visibleColumnsForTable: Array<ColumnDef<TData>>;
}): Set<string> => {
  const tableColumns = visibleColumnsForTable.find(
    (col) => col.id === 'table'
  ) as GroupColumnDef<TData>;
  if (tableColumns == null || tableColumns.columns == null) {
    return new Set();
  }

  return new Set(tableColumns.columns.map((col) => String(col.id)) ?? []);
};

export const formatNameValue = ({
  firstName,
  lastName,
  middleName,
}: {
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  middleName?: Nullable<string>;
}): string =>
  `${!isEmptyStringOrNull(lastName) ? lastName : 'UNKNOWN'}, ${!isEmptyStringOrNull(firstName) ? firstName : 'UNKNOWN'}${middleName != null ? ` ${middleName}` : ''}`;

export const formatUserDisplayName = ({
  firstName,
  lastName,
  email,
}: {
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  email: Nullable<string>;
}): string => {
  const emailPrefix = email != null && email.trim() !== '' ? email.split('@')[0] : null;
  const nameParts = [firstName, lastName].filter((p) => p != null && p !== '');
  const name = nameParts.length > 0 ? nameParts.join(' ') : 'UNKNOWN';
  return emailPrefix != null ? `${name} (${emailPrefix})` : name;
};

export const getReferringPhysicianName = (row: WorklistItemData) => {
  const { referringPhysicianFirstName, referringPhysicianLastName, referringPhysicianMiddleName } =
    row;

  let nameToDisplay = null;

  if (
    !isEmptyStringOrNull(referringPhysicianFirstName) ||
    !isEmptyStringOrNull(referringPhysicianLastName) ||
    !isEmptyStringOrNull(referringPhysicianMiddleName)
  ) {
    nameToDisplay = formatNameValue({
      firstName: referringPhysicianFirstName,
      lastName: referringPhysicianLastName,
      middleName: referringPhysicianMiddleName,
    });
  } else if (!isEmptyStringOrNull(row?.referringPhysician)) {
    nameToDisplay = row?.referringPhysician;
  } else {
    nameToDisplay = 'N/A';
  }

  return nameToDisplay;
};

const VALID_WORKLIST_COLUMN_IDS = new Set(Object.values(WorklistColumnId) as WorklistColumnId[]);

export const isValidColumnID = (columnId: string): boolean => {
  return VALID_WORKLIST_COLUMN_IDS.has(columnId as WorklistColumnId);
};
